import { useQuery } from '@tanstack/react-query';
import { needsLicenceAttention } from '@wodi/domain';
import type { MyPractitionerView } from '@wodi/shared';
import { CriticalStrip } from '@wodi/ui';

import { myPractitionerQuery } from '../api/practitioners.ts';
import { dayText, licenceNoun } from './format.tsx';

// The signed-in clinician's own licence (docs/plans/phase2-staff-records.md §1.1, §6): a
// passive strip on every screen while it has expired or expires within the organisation's
// window. It never blocks and has no button: it goes away when the licence is renewed.
// Whether an expired licence should block clinical actions is TODO(clinical-verify) S1.

export function licenceStripText(me: MyPractitionerView): string | null {
  if (!me.hasProfile || !needsLicenceAttention(me.licence.state)) return null;
  const noun = `Your ${licenceNoun(me.licenceBody).replace(/^Licence$/, 'licence')}`;
  const day = dayText(me.licence.expiresOn);
  const after = 'Renew it with the council and give the new details to your administrator.';
  if (me.licence.state === 'expired') return `${noun} expired on ${day}. ${after}`;
  const left = me.licence.daysLeft ?? 0;
  const when = left === 0 ? 'today' : left === 1 ? 'tomorrow' : `in ${String(left)} days`;
  return `${noun} expires on ${day}, ${when}. ${after}`;
}

export function MyLicenceStrip() {
  const me = useQuery(myPractitionerQuery);
  // Passive: if it can't be loaded, nothing is shown; Finish consultation still checks.
  if (!me.isSuccess) return null;
  const text = licenceStripText(me.data);
  if (text === null) return null;
  return <CriticalStrip label="Your licence">{text}</CriticalStrip>;
}
